import DefaultLayout from '@/components/layouts/DefaultLayout';
import TrialProvider from '@/components/providers/data/TrialProvider';
import Configurations from '@/pages/Configurations';
import EditConfiguration from '@/pages/EditConfiguration';
import NewConfiguration from '@/pages/NewConfiguration';
import Notes from '@/pages/Notes';
import Patient from '@/pages/Patient';
import PatientData from '@/pages/PatientData';
import PatientDataRealTime from '@/pages/PatientDataRealTime';
import PatientDataView from '@/pages/PatientDataView';
import PatientNotes from '@/pages/PatientNotes';
import Patients from '@/pages/Patients';
import PreviewConfiguration from '@/pages/PreviewConfiguration';
import TrialConfiguration from '@/pages/TrialConfiguration';
import Trials from '@/pages/Trials';
import { AuthenticatedUserContext } from '@ngt/forms';
import * as Sentry from "@sentry/react";
import { FC, Suspense, useContext, useEffect } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom';
import { createSentryAnonymousUser } from './error';
import formsFeature from './formsFeature';

// const Trials = lazy(() => import('@/pages/Trials'));
// const Patients = lazy(() => import('@/pages/Patients'));
// const Configurations = lazy(() => import('@/pages/Configurations'));
// const Configuration = lazy(() => import('@/pages/Configuration'));

const Test: FC<{test: string;}> = ({ test }) => {
    return (
        <>
            {test}
            <Outlet />
        </>
    );
};

const App: FC = () => {
    const { refresh, mutate, data: user } = useContext(AuthenticatedUserContext);

    useEffect(() => {
        if (!user) {
            Sentry.setUser(createSentryAnonymousUser());
            return;
        }

        Sentry.setUser({
            id: user?.userName,
            username: user?.userName,
            fullName: user?.displayName,
            authenticated: true
        })

        return () => {
            Sentry.setUser(createSentryAnonymousUser());
        }
    }, [user])

    useEffect(() => {
        formsFeature.serviceStackClient.responseFilter = (res: Response) => {        
            if (res.headers.has('openiddict-user-status') &&
                res.headers.get('openiddict-user-status') === 'unauthenticated' &&
                user) {
                mutate(undefined, false);
            }
            else if (res.headers.has('openiddict-user-status') &&
                res.headers.get('openiddict-user-status') === 'true') {
                    refresh();
            }
        }
        
        formsFeature.serviceStackClient.exceptionFilter = (res: Response, error: any) => {
            if (res.status === 401) {
                window.location.href = `/auth/openiddict?redirect=${encodeURIComponent(global.location.pathname + global.location.search?.replace(/&/, '%26'))}`;
            }
            else if (res.status === 403) {
                window.location.href = '/error/403';
            }
        };
    }, [refresh, user ])

    return (
        <>
            <BrowserRouter>
                <TrialProvider>
                    <Suspense>
                        <Routes>
                            <Route path="/:trialCode/configurations/preview" element={<PreviewConfiguration />} />
                            <Route path="/" element={<DefaultLayout />}>
                                <Route index element={<Trials />} />
                                <Route path=":trialCode">
                                    <Route index element={<Trials />} />
                                    <Route path="participants">
                                        <Route index element={<Patients />} />
                                        <Route path=":patientStudyNumber">
                                            <Route index element={<Patient />} />
                                            <Route path="data">
                                                <Route index element={<PatientData />} />
                                                <Route path=":configurationId">
                                                    <Route path="real-time" element={<PatientDataRealTime />} />
                                                    <Route path=":sourceDataId" element={<PatientDataView />} />
                                                </Route>
                                                <Route path="all">
                                                    <Route path="real-time" element={<PatientDataRealTime />} />
                                                    <Route path=":sourceDataId" element={<PatientDataView />} />
                                                </Route>
                                            </Route>
                                            <Route path="discrepancy-notes">
                                                <Route index element={<PatientNotes />} />
                                            </Route>
                                        </Route>
                                    </Route>
                                    <Route path="configurations">
                                        <Route index element={<Configurations />} />
                                        <Route path="new" element={<NewConfiguration />} />
                                        <Route path="trial" element={<TrialConfiguration />} />
                                        <Route path=":configurationId" element={<EditConfiguration />} />
                                        <Route path="preview" element={<PreviewConfiguration />} />
                                    </Route>
                                    <Route path="discrepancy-notes">
                                        <Route index element={<Notes />} />
                                    </Route>
                                </Route>
                                <Route path="*" element={<Test test="Not Found"/>} />
                            </Route>
                        </Routes>
                    </Suspense>
                </TrialProvider>
            </BrowserRouter>
        </>
    )
}

export default App
